'use client';

import { useCallback, useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';

const LOCATION_EXPIRATION_MS = 5 * 60 * 1000; // 5 minutes

function parseStoredLocation(storedRaw: string): { path: string; timestamp: number | null } {
  try {
    const parsed = JSON.parse(storedRaw);
    if (typeof parsed === 'string') {
      return { path: parsed, timestamp: null };
    }
    return { path: parsed.path, timestamp: parsed.timestamp ?? null };
  } catch {
    return { path: storedRaw, timestamp: null };
  }
}

export function usePreserveLocation() {
  const pathname = usePathname();
  const restoreAttemptedRef = useRef<string | null>(null);
  const userNavigatedAwayRef = useRef(false);
  const restoreCountRef = useRef<number>(0);
  const lastRestoreTimeRef = useRef<number>(0);

  // Clear any existing stored location on initialization (location storage is disabled)
  useEffect(() => {
    if (typeof window !== 'undefined') {
      sessionStorage.removeItem('last_app_location');
    }
  }, []);

  // Validate if a path is well-formed (no duplicate slugs, proper format)
  const isValidPath = useCallback((path: string): boolean => {
    if (!path || path === '/') return true;

    // Check for duplicate /t/ slugs (e.g., /t/admin/t/admin/settings)
    const tMatches = path.match(/\/t\//g);
    if (tMatches && tMatches.length > 1) {
      console.warn(' [LOCATION] Invalid path detected - duplicate /t/ slugs:', path);
      return false;
    }

    // Check for malformed paths with multiple consecutive slashes (except // at start for protocol)
    if (path.includes('//') && !path.startsWith('//')) {
      console.warn(' [LOCATION] Invalid path detected - multiple consecutive slashes:', path);
      return false;
    }

    return true;
  }, []);

  // Store current location whenever user navigates (except auth/system pages)
  // DISABLED: Location storage is disabled to prevent infinite loops
  useEffect(() => {
    // Location storage is disabled - do nothing
    return;
  }, [pathname]);

  // Function to restore location
  // DISABLED: Location restoration is disabled to prevent infinite loops
  const restoreLocation = useCallback(() => {
    // Clear any existing stored location to prevent issues
    sessionStorage.removeItem('last_app_location');
    return false; // Always return false - restoration is disabled
  }, []);

  // Function to clear stored location (useful for logout)
  const clearStoredLocation = useCallback(() => {
    const current = sessionStorage.getItem('last_app_location');
    sessionStorage.removeItem('last_app_location');
    restoreAttemptedRef.current = null;
    userNavigatedAwayRef.current = false;
    restoreCountRef.current = 0;
    lastRestoreTimeRef.current = 0;
    console.log(' [LOCATION] Cleared stored location (was:', current, ')');
  }, []);

  // Function to mark that user is navigating away (prevents re-storing bad locations)
  const markUserNavigatedAway = useCallback(() => {
    userNavigatedAwayRef.current = true;
    restoreAttemptedRef.current = null;
    console.log(' [LOCATION] Marked user navigation away - will skip next storage');
  }, []);

  // Function to validate and clean stored location
  const validateAndCleanStoredLocation = useCallback((): boolean => {
    const storedRaw = sessionStorage.getItem('last_app_location');
    if (!storedRaw) return false;

    const { path: stored, timestamp: storedTimestamp } = parseStoredLocation(storedRaw);

    if (storedTimestamp && Date.now() - storedTimestamp > LOCATION_EXPIRATION_MS) {
      console.warn(' [LOCATION] Cleaning expired stored location:', stored);
      sessionStorage.removeItem('last_app_location');
      restoreAttemptedRef.current = null;
      return false;
    }

    if (!isValidPath(stored)) {
      console.warn(' [LOCATION] Cleaning invalid stored location:', stored);
      sessionStorage.removeItem('last_app_location');
      restoreAttemptedRef.current = null;
      return false;
    }

    return true;
  }, [isValidPath]);

  const hasStoredLocation = useCallback(() => {
    const storedRaw = sessionStorage.getItem('last_app_location');
    if (!storedRaw) return false;

    const { path: stored, timestamp: storedTimestamp } = parseStoredLocation(storedRaw);

    if (storedTimestamp && Date.now() - storedTimestamp > LOCATION_EXPIRATION_MS) {
      sessionStorage.removeItem('last_app_location');
      return false;
    }

    return isValidPath(stored);
  }, [isValidPath]);

  const getCurrentStoredLocation = useCallback(() => {
    const storedRaw = sessionStorage.getItem('last_app_location');
    if (!storedRaw) return null;

    const { path: stored, timestamp: storedTimestamp } = parseStoredLocation(storedRaw);

    if (storedTimestamp && Date.now() - storedTimestamp > LOCATION_EXPIRATION_MS) {
      sessionStorage.removeItem('last_app_location');
      return null;
    }

    return isValidPath(stored) ? stored : null;
  }, [isValidPath]);

  const forceStoreLocation = useCallback((path: string) => {
    if (isValidPath(path)) {
      const locationData = {
        path: path,
        timestamp: Date.now(),
      };
      sessionStorage.setItem('last_app_location', JSON.stringify(locationData));
      console.log(' [LOCATION] Manually stored location:', path);
    } else {
      console.warn(' [LOCATION] Cannot store invalid path:', path);
    }
  }, [isValidPath]);

  return {
    restoreLocation,
    clearStoredLocation,
    markUserNavigatedAway,
    validateAndCleanStoredLocation,
    isValidPath,
    hasStoredLocation,
    getCurrentStoredLocation,
    forceStoreLocation,
  };
}
